import { AccessDeniedPage } from '@/components/error-boundary/AccessDeniedPage'
import { useCanManageCoaching } from '@/hooks/auth/useCanManageCoaching'
import { useRole } from '@/hooks/useRole'
import { ClerkUserRole, ROUTE_ACCESS_CONFIG, RouteCapability } from '@/types/auth'
import { SignedIn } from '@clerk/clerk-react'
import { useLocation } from 'react-router'

type Props = {
  readonly children: React.ReactNode
  readonly allowedRoles?: readonly ClerkUserRole[]
}

type CapabilityState = {
  readonly isLoaded: boolean
  readonly granted: boolean
}

export function ProtectedRoute({ children, allowedRoles }: Props) {
  const { userRole, hasRole } = useRole()
  const coachingManagement = useCanManageCoaching()
  const { pathname } = useLocation()

  const capabilities: Readonly<Record<RouteCapability, CapabilityState>> = {
    [RouteCapability.MANAGE_COACHING]: {
      isLoaded: coachingManagement.isLoaded,
      granted: coachingManagement.canManageCoaching,
    },
  }

  return (
    <SignedIn>
      {(() => {
        if (!hasRole || !userRole) {
          return <AccessDeniedPage />
        }

        if (allowedRoles) {
          return allowedRoles.includes(userRole) ? children : <AccessDeniedPage />
        }

        const matchingConfig = ROUTE_ACCESS_CONFIG.find((config) =>
          config.routes.some((routePattern) => {
            const pattern = routePattern.toString()

            // Handle wildcard patterns (**) for nested routes. Anchored on a
            // segment boundary so `/admin/coaching/**` cannot also admit
            // `/admin/coaching-reports`.
            if (pattern.endsWith('/**')) {
              const basePattern = pattern.slice(0, -3) // Remove /**
              return pathname === basePattern || pathname.startsWith(`${basePattern}/`)
            }

            // Handle exact pattern matches
            return pathname === pattern
          })
        )

        // No matching config denies access.
        if (!matchingConfig) {
          return <AccessDeniedPage />
        }

        if (matchingConfig.allowedRoles.includes(userRole)) {
          return children
        }

        if (matchingConfig.capability) {
          const capability = capabilities[matchingConfig.capability]
          // Nothing while it loads, so a manager never sees Access Denied flash.
          if (!capability.isLoaded) return null
          return capability.granted ? children : <AccessDeniedPage />
        }

        return <AccessDeniedPage />
      })()}
    </SignedIn>
  )
}
